import React from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import PermissionBubble from "../PermissionBubble";
import SettingsItem from "./SettingsItem/SettingsItem";
import TagsItem from "./TagsItem/TagsItem";
import AdditionalIbosField from "./AdditionalIbosField";
import { getTagFieldConfig, getTagTitleFromTypeCode } from "./tagSchema";
import {
  createDreamcastContentSettingsTagOption,
  deleteDreamcastContentSettingsTagOption,
} from "DreamCast/api/media";
import styles from "./ContentSettingsCard.module.css";

const MEMBERSHIP_OPTIONS_FALLBACK = [
  { id: "complimentary", label: "C", fullLabel: "Complimentary Members" },
  { id: "premier", label: "P", fullLabel: "Active Premier Members" },
];

const PIN_LEVELS_FALLBACK = [
  { id: "ibo", label: "IBO", fullLabel: "Active Associate IBOs" },
  { id: "platinum", label: "PLA", fullLabel: "Active Platinums" },
  { id: "emerald", label: "EME", fullLabel: "Active Emeralds" },
  { id: "diamond", label: "DIA", fullLabel: "Active Diamonds" },
];

const MEMBERSHIP_TONE_BY_ID = {
  complimentary: "gray",
  premier: "teal",
};

const PIN_TONE_BY_ID = {
  ibo: "gray",
  platinum: "platinum",
  emerald: "emeraldDark",
  diamond: "teal",
};

const mapToSelectionObject = (values) =>
  (values || []).reduce((acc, value) => {
    acc[value] = true;
    return acc;
  }, {});

const selectionObjectToArray = (selection) =>
  Object.keys(selection || {}).filter((key) => !!selection[key]);

const resolveTagValues = (tags, typeCode) => {
  const config = getTagFieldConfig(typeCode);

  if (config?.valueKeys?.length) {
    for (const key of config.valueKeys) {
      if (Array.isArray(tags?.[key])) {
        return tags[key];
      }
    }
  }

  if (Array.isArray(tags?.byTypeCode?.[typeCode])) {
    return tags.byTypeCode[typeCode];
  }

  return [];
};

const resolveTagOptions = (tags, typeCode, definitionOptions = []) => {
  const values = [];
  const seen = new Set();

  const append = (items = []) => {
    for (const item of items) {
      const label = String(item || "").trim();
      if (!label || seen.has(label)) {
        continue;
      }

      seen.add(label);
      values.push(label);
    }
  };

  append(definitionOptions);

  const config = getTagFieldConfig(typeCode);
  if (config?.optionKeys?.length) {
    for (const key of config.optionKeys) {
      append(tags?.[key]);
    }
  }

  append(resolveTagValues(tags, typeCode));

  return values;
};

const applyTagValues = (tags, typeCode, nextValues) => {
  const config = getTagFieldConfig(typeCode);

  if (config?.valueKeys?.length) {
    return config.valueKeys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: [...nextValues],
      }),
      { ...(tags || {}) },
    );
  }

  return {
    ...(tags || {}),
    byTypeCode: {
      ...(tags?.byTypeCode || {}),
      [typeCode]: [...nextValues],
    },
  };
};

const applyTagOptions = (tags, typeCode, nextOptions) => {
  const config = getTagFieldConfig(typeCode);
  if (!config?.optionKeys?.length) {
    return tags || {};
  }

  return config.optionKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: [...nextOptions],
    }),
    { ...(tags || {}) },
  );
};

const FALLBACK_TAG_DEFINITION_KEYS = [
  {
    typeCode: "LOCATION",
    optionKeys: ["locationOptions"],
    valueKeys: ["selectedLocations", "locations"],
    componentTypeCode: "CHECKBOX_LIST",
  },
  {
    typeCode: "LANGUAGE",
    optionKeys: ["languageOptions"],
    valueKeys: ["selectedLanguages", "languages"],
    componentTypeCode: "CHECKBOX_LIST",
  },
  {
    typeCode: "OCCUPATION",
    optionKeys: ["occupationOptions"],
    valueKeys: ["selectedOccupations", "occupations"],
    componentTypeCode: "CHECKBOX_LIST",
  },
  {
    typeCode: "KEYWORD_TAG",
    optionKeys: ["keywordOptions"],
    valueKeys: ["keywords"],
    componentTypeCode: "CHIP_SELECTOR_MULTI",
  },
  {
    typeCode: "HIDDEN_TAG",
    optionKeys: ["hiddenTagOptions"],
    valueKeys: ["hiddenTags"],
    componentTypeCode: "CHIP_SELECTOR_MULTI",
  },
];

const buildFallbackTagDefinitionsFromTags = (tags = {}) => {
  return FALLBACK_TAG_DEFINITION_KEYS.map((entry) => {
    const options = entry.optionKeys
      .flatMap((key) => (Array.isArray(tags?.[key]) ? tags[key] : []))
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    const selectedValues = entry.valueKeys
      .flatMap((key) => (Array.isArray(tags?.[key]) ? tags[key] : []))
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (options.length === 0 && selectedValues.length === 0) {
      return null;
    }

    return {
      attributeTypeCode: entry.typeCode,
      attributeType: getTagTitleFromTypeCode(entry.typeCode),
      componentTypeCode: entry.componentTypeCode,
      options,
    };
  }).filter(Boolean);
};

export function ContentSettingsCard({
  membershipPermissions,
  pinPermissions,
  membershipGroupValuesById,
  familyReunionGroups,
  selectedFamilyGroups,
  diamonds,
  selectedDiamonds,
  additionalVisibilitySections,
  visibilitySelectionsByTypeCode,
  familyReunionSectionTitle,
  diamondsSectionTitle,
  additionalIbos,
  tags,
  onChange,
  membershipOptions,
  selectedMemberships,
  onMembershipChange,
  pinLevels,
  selectedPinLevels,
  onPinLevelChange,
  settingsItems,
  tagsItem,
  tagsOnly,
  onTagCreated,
}) {
  const isSchemaDriven = !!(
    membershipPermissions ||
    pinPermissions ||
    onChange
  );
  const [addTagErrorMessage, setAddTagErrorMessage] = React.useState("");

  const resolvedMembershipOptions =
    Array.isArray(membershipOptions) && membershipOptions.length > 0
      ? membershipOptions
      : MEMBERSHIP_OPTIONS_FALLBACK;

  const resolvedPinLevels =
    Array.isArray(pinLevels) && pinLevels.length > 0
      ? pinLevels
      : PIN_LEVELS_FALLBACK;

  const resolvedSelectedMemberships = isSchemaDriven
    ? resolvedMembershipOptions
        .filter((item) => !!membershipPermissions?.[item.id])
        .map((item) => item.id)
    : selectedMemberships || [];

  const resolvedSelectedPinLevels = isSchemaDriven
    ? resolvedPinLevels
        .filter((item) => !!pinPermissions?.[item.id])
        .map((item) => item.id)
    : selectedPinLevels || [];

  const emitChange = (nextValues) => {
    if (!onChange) return;
    onChange({
      membershipPermissions: membershipPermissions || {
        complimentary: true,
        premier: true,
      },
      pinPermissions: pinPermissions || {
        ibo: true,
        platinum: true,
        emerald: true,
        diamond: true,
      },
      membershipOptions: membershipOptions || [],
      pinLevels: pinLevels || [],
      membershipGroupValuesById: membershipGroupValuesById || {
        premier: 1,
        complimentary: 2,
      },
      familyReunionGroups: familyReunionGroups || [],
      selectedFamilyGroups: selectedFamilyGroups || [],
      familyReunionSectionTitle: familyReunionSectionTitle || "",
      diamonds: diamonds || [],
      selectedDiamonds: selectedDiamonds || [],
      diamondsSectionTitle: diamondsSectionTitle || "",
      additionalVisibilitySections: additionalVisibilitySections || [],
      visibilitySelectionsByTypeCode: visibilitySelectionsByTypeCode || {},
      additionalIbos: additionalIbos || "",
      tags: tags || {
        locations: [],
        languages: [],
        occupations: [],
        keywords: [],
        hiddenTags: [],
      },
      ...nextValues,
    });
  };

  const handleMembershipToggle = (id) => () => {
    if (isSchemaDriven) {
      const next = {
        ...(membershipPermissions || {}),
        [id]: !membershipPermissions?.[id],
      };
      // Per the data owner: a type can never have zero selections — none
      // selected is meaningless (equivalent to all). Block the last deselect.
      if (!Object.values(next).some(Boolean)) {
        return;
      }
      emitChange({
        membershipPermissions: next,
      });
      return;
    }

    if (onMembershipChange) {
      const next = new Set(selectedMemberships || []);
      next.has(id) ? next.delete(id) : next.add(id);
      onMembershipChange(Array.from(next));
    }
  };

  const handlePinToggle = (id) => () => {
    if (isSchemaDriven) {
      const next = {
        ...(pinPermissions || {}),
        [id]: !pinPermissions?.[id],
      };
      // Same never-empty rule as memberships.
      if (!Object.values(next).some(Boolean)) {
        return;
      }
      emitChange({
        pinPermissions: next,
      });
      return;
    }

    if (onPinLevelChange) {
      const next = new Set(selectedPinLevels || []);
      next.has(id) ? next.delete(id) : next.add(id);
      onPinLevelChange(Array.from(next));
    }
  };

  const builtInSettingsItems = isSchemaDriven
    ? (() => {
        const items = [];

        (additionalVisibilitySections || []).forEach((section) => {
          const typeCode = String(section?.typeCode || "").toUpperCase();
          const title = String(section?.title || "").trim();
          const rawOptions = Array.isArray(section?.options)
            ? section.options
            : [];

          // Deduplicate options
          const options = Array.from(new Set(rawOptions));

          if (!typeCode || !title) {
            return;
          }

          // AI-TODO: Consider hiding this section when `options.length === 0`.
          // Current behavior intentionally renders empty DB-defined sections (e.g. EVENT_DIAMOND).
          // Search hint for future agents: "AI-TODO empty visibility section render".

          const selectedValues = Array.isArray(
            visibilitySelectionsByTypeCode?.[typeCode],
          )
            ? visibilitySelectionsByTypeCode[typeCode]
            : [];

          items.push({
            id: `visibility-${typeCode}`,
            title,
            options,
            columns: 2,
            selectedOptions: mapToSelectionObject(selectedValues),
            onChange: (selection) =>
              emitChange({
                visibilitySelectionsByTypeCode: {
                  ...(visibilitySelectionsByTypeCode || {}),
                  [typeCode]: selectionObjectToArray(selection),
                },
              }),
          });
        });

        items.push({
          id: "additionalIbos",
          title:
            "Additional IBOs (These IBOs will be added to view content outside of their pin level)",
          renderContent: () => (
            <AdditionalIbosField
              value={additionalIbos || ""}
              onChange={(next) => emitChange({ additionalIbos: next })}
            />
          ),
        });

        return items;
      })()
    : settingsItems;

  const builtInTagsItem = isSchemaDriven
    ? (() => {
        const sourceDefinitionsFromSchema =
          Array.isArray(tags?.tagDefinitions) && tags.tagDefinitions.length > 0
            ? tags.tagDefinitions
            : [];
        const sourceDefinitions =
          sourceDefinitionsFromSchema.length > 0
            ? sourceDefinitionsFromSchema
            : buildFallbackTagDefinitionsFromTags(tags);

        if (sourceDefinitions.length === 0) {
          return null;
        }

        const sections = sourceDefinitions
          .map((definition) => {
            const typeCode = String(
              definition?.attributeTypeCode || definition?.AttributeTypeCode,
            )
              .trim()
              .toUpperCase();

            if (!typeCode) {
              return null;
            }

            const componentType = String(
              definition?.componentTypeCode || definition?.ComponentTypeCode,
            )
              .trim()
              .toUpperCase();

            const rawTitle =
              definition?.attributeType || definition?.AttributeType || "";
            const title =
              String(rawTitle).trim() || getTagTitleFromTypeCode(typeCode);

            const definitionOptions = Array.isArray(definition?.options)
              ? definition.options
              : Array.isArray(definition?.Attributes)
              ? definition.Attributes.map((item) =>
                  String(item?.Attribute || "").trim(),
                ).filter(Boolean)
              : [];

            const selectedValues = resolveTagValues(tags, typeCode);
            const options = resolveTagOptions(
              tags,
              typeCode,
              definitionOptions,
            );
            const isLanguageSection = typeCode === "LANGUAGE";
            const languageFallback =
              options.find(
                (option) => String(option).toLowerCase() === "english",
              ) ||
              options[0] ||
              "English";
            const effectiveSelectedValues = isLanguageSection
              ? selectedValues.length > 0
                ? [selectedValues[0]]
                : [languageFallback]
              : selectedValues;

            const toggleValue = (value, checked) => {
              if (isLanguageSection) {
                const nextValues = checked ? [value] : [languageFallback];
                emitChange({
                  tags: applyTagValues(tags, typeCode, nextValues),
                });
                return;
              }

              const current = new Set(
                componentType === "CHIP_SELECTOR_MULTI"
                  ? effectiveSelectedValues
                  : resolveTagValues(tags, typeCode),
              );
              checked ? current.add(value) : current.delete(value);

              emitChange({
                tags: applyTagValues(tags, typeCode, Array.from(current)),
              });
            };

            const addValue = async (inputValue) => {
              const normalizedValue = String(inputValue || "").trim();
              if (!normalizedValue) {
                return false;
              }

              const tagField = getTagFieldConfig(typeCode)?.optionKeys?.[0];

              if (tagField) {
                try {
                  await createDreamcastContentSettingsTagOption(
                    tagField,
                    normalizedValue,
                  );
                  setAddTagErrorMessage("");
                  // Trigger refetch of config after tag is successfully created
                  if (typeof onTagCreated === "function") {
                    onTagCreated();
                  }
                } catch (error) {
                  setAddTagErrorMessage(
                    error?.message || "Failed to add tag globally.",
                  );
                  return false;
                }
              }

              const currentOptions = resolveTagOptions(
                tags,
                typeCode,
                definitionOptions,
              );
              const existingOption = currentOptions.find(
                (option) =>
                  String(option).toLowerCase() ===
                  normalizedValue.toLowerCase(),
              );
              const resolvedValue = existingOption || normalizedValue;

              const nextOptions = existingOption
                ? currentOptions
                : [...currentOptions, normalizedValue];

              const nextSelectedValues = isLanguageSection
                ? [resolvedValue]
                : Array.from(
                    new Set([
                      ...resolveTagValues(tags, typeCode),
                      resolvedValue,
                    ]),
                  );

              const nextTags = applyTagOptions(
                applyTagValues(tags, typeCode, nextSelectedValues),
                typeCode,
                nextOptions,
              );

              emitChange({
                tags: nextTags,
              });

              return true;
            };

            return {
              id: typeCode,
              title,
              componentType: componentType || "CHECKBOX_LIST",
              options,
              selectedValues: effectiveSelectedValues,
              onToggle: toggleValue,
              onAdd: addValue,
              onDelete: async (value) => {
                // Create the tag option ID in the format: TYPECODE::value
                const tagOptionId = `${typeCode}::${value}`;

                try {
                  // Call the API to delete from database
                  await deleteDreamcastContentSettingsTagOption(tagOptionId);
                } catch (error) {
                  // If the tag doesn't exist on server (404), continue with local deletion
                  if (error?.status !== 404) {
                    console.error(`Failed to delete tag "${value}":`, error);
                    throw error;
                  }
                  console.warn(`Tag "${value}" not found on server, removing from local state`);
                }

                // Update local state after deletion (successful or 404)
                const current = new Set(effectiveSelectedValues);
                current.delete(value);
                emitChange({
                  tags: applyTagValues(tags, typeCode, Array.from(current)),
                });

                // Trigger refetch of config after tag is successfully deleted
                if (typeof onTagCreated === "function") {
                  onTagCreated();
                }
              },
            };
          })
          .filter(Boolean);

        if (sections.length === 0) {
          return null;
        }

        // Reorder sections: LOCATION, LANGUAGE, OCCUPATION, KEYWORD_TAG, then others
        const sectionOrder = [
          "LOCATION",
          "LANGUAGE",
          "OCCUPATION",
          "KEYWORD_TAG",
        ];
        const orderedSections = sections.sort((a, b) => {
          const aIndex = sectionOrder.indexOf(a.id);
          const bIndex = sectionOrder.indexOf(b.id);

          // If both are in order, sort by order
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only a is in order, it comes first
          if (aIndex !== -1) {
            return -1;
          }
          // If only b is in order, it comes first
          if (bIndex !== -1) {
            return 1;
          }
          // If neither are in order, maintain their relative order
          return 0;
        });

        return {
          title: "Tags, etc",
          sections: orderedSections,
        };
      })()
    : tagsItem;

  return (
    <Card
      className={`${styles.card} ${tagsOnly ? styles.tagsOnlyCard : ""}`.trim()}
    >
      <div className={styles.headerRow}>
        <span className={styles.title}>Content Settings</span>
      </div>
      <div className={styles.headerDivider} />

      {!tagsOnly && (
        <div className={styles.permissionsRow}>
          <div className={styles.permissionBlock}>
            <span className={styles.sectionLabel}>Membership Permissions</span>
            <div className={styles.chipRow}>
              {resolvedMembershipOptions.map((item) => {
                const isActive = resolvedSelectedMemberships.includes(item.id);
                return (
                  <PermissionBubble
                    key={item.id}
                    label={item.label}
                    iconSrc={item.icon}
                    iconAlt={item.fullLabel || item.label}
                    active={isActive}
                    size="lg"
                    tone={MEMBERSHIP_TONE_BY_ID[item.id] || "teal"}
                    onClick={handleMembershipToggle(item.id)}
                  />
                );
              })}
            </div>
          </div>

          <div className={styles.permissionBlock}>
            <span className={styles.sectionLabel}>Pin Level Permissions</span>
            <div className={styles.pinRow}>
              {resolvedPinLevels.map((pin) => {
                const isActive = resolvedSelectedPinLevels.includes(pin.id);
                return (
                  <PermissionBubble
                    key={pin.id}
                    label={pin.label}
                    iconSrc={pin.icon}
                    iconAlt={pin.fullLabel || pin.label}
                    active={isActive}
                    size="lg"
                    tone={PIN_TONE_BY_ID[pin.id] || "teal"}
                    onClick={handlePinToggle(pin.id)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className={styles.settingsList}>
        {addTagErrorMessage ? (
          <div className={styles.warningNotice}>{addTagErrorMessage}</div>
        ) : null}
        {!tagsOnly &&
          builtInSettingsItems.map((item) => {
            const content =
              typeof item.renderContent === "function"
                ? item.renderContent()
                : item.content;
            return (
              <SettingsItem
                key={item.id}
                title={item.title}
                options={item.options}
                columns={item.columns}
                selectedOptions={item.selectedOptions}
                onChange={item.onChange}
              >
                {content}
              </SettingsItem>
            );
          })}
        {builtInTagsItem && <TagsItem {...builtInTagsItem} />}
      </div>
    </Card>
  );
}

ContentSettingsCard.propTypes = {
  membershipPermissions: PropTypes.shape({
    complimentary: PropTypes.bool,
    premier: PropTypes.bool,
  }),
  pinPermissions: PropTypes.shape({
    ibo: PropTypes.bool,
    platinum: PropTypes.bool,
    emerald: PropTypes.bool,
    diamond: PropTypes.bool,
  }),
  membershipGroupValuesById: PropTypes.shape({
    premier: PropTypes.number,
    complimentary: PropTypes.number,
  }),
  familyReunionGroups: PropTypes.arrayOf(PropTypes.string),
  selectedFamilyGroups: PropTypes.arrayOf(PropTypes.string),
  diamonds: PropTypes.arrayOf(PropTypes.string),
  selectedDiamonds: PropTypes.arrayOf(PropTypes.string),
  additionalVisibilitySections: PropTypes.arrayOf(
    PropTypes.shape({
      typeCode: PropTypes.string,
      title: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  visibilitySelectionsByTypeCode: PropTypes.object,
  familyReunionSectionTitle: PropTypes.string,
  diamondsSectionTitle: PropTypes.string,
  additionalIbos: PropTypes.string,
  tags: PropTypes.shape({
    locations: PropTypes.arrayOf(PropTypes.string),
    locationOptions: PropTypes.arrayOf(PropTypes.string),
    selectedLocations: PropTypes.arrayOf(PropTypes.string),
    languages: PropTypes.arrayOf(PropTypes.string),
    languageOptions: PropTypes.arrayOf(PropTypes.string),
    selectedLanguages: PropTypes.arrayOf(PropTypes.string),
    occupations: PropTypes.arrayOf(PropTypes.string),
    occupationOptions: PropTypes.arrayOf(PropTypes.string),
    selectedOccupations: PropTypes.arrayOf(PropTypes.string),
    keywords: PropTypes.arrayOf(PropTypes.string),
    keywordOptions: PropTypes.arrayOf(PropTypes.string),
    hiddenTags: PropTypes.arrayOf(PropTypes.string),
    hiddenTagOptions: PropTypes.arrayOf(PropTypes.string),
    byTypeCode: PropTypes.object,
    tagDefinitions: PropTypes.arrayOf(
      PropTypes.shape({
        attributeTypeCode: PropTypes.string,
        attributeType: PropTypes.string,
        componentTypeCode: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
      }),
    ),
  }),
  onChange: PropTypes.func,
  membershipOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      fullLabel: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  selectedMemberships: PropTypes.arrayOf(PropTypes.string),
  onMembershipChange: PropTypes.func,
  pinLevels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      fullLabel: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  selectedPinLevels: PropTypes.arrayOf(PropTypes.string),
  onPinLevelChange: PropTypes.func,
  settingsItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.string),
      selectedOptions: PropTypes.object,
      onChange: PropTypes.func,
      renderContent: PropTypes.func,
      content: PropTypes.node,
    }),
  ),
  tagsItem: PropTypes.object,
  tagsOnly: PropTypes.bool,
  onTagCreated: PropTypes.func,
};

ContentSettingsCard.defaultProps = {
  membershipPermissions: undefined,
  pinPermissions: undefined,
  membershipGroupValuesById: undefined,
  familyReunionGroups: [],
  selectedFamilyGroups: [],
  diamonds: [],
  selectedDiamonds: [],
  additionalVisibilitySections: [],
  visibilitySelectionsByTypeCode: {},
  familyReunionSectionTitle: "",
  diamondsSectionTitle: "",
  additionalIbos: "",
  tags: {
    locations: [],
    languages: [],
    occupations: [],
    keywords: [],
    hiddenTags: [],
    byTypeCode: {},
    tagDefinitions: [],
  },
  onChange: undefined,
  membershipOptions: [],
  selectedMemberships: [],
  pinLevels: [],
  selectedPinLevels: [],
  settingsItems: [],
  tagsItem: undefined,
  tagsOnly: false,
};
